import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  input,
  output,
  viewChildren,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import {
  concatMap,
  finalize,
  from,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AnalyticsService, EditorShellComponent, EditorWorkspaceComponent, ObjectUrlHolder, readFile } from '@imagise/shared';
import {
  EditService,
  MergeService,
  VisionObjectDetectionService,
  type FlexMergeImageRequest,
  type GridMergeImageRequest,
  type MergeImageRequest,
} from '@imagise/generated-imagise-cloud-api-client';
import {
  AppError,
  attachmentFromBase64,
  dispatchEditImageRequest,
  EditMode,
  EditorArrangeTileInput,
  ErrorResponse,
  ErrorReportOperation,
  FileResult,
  MainMenuComponent,
  serializeEditImageRequest,
} from '@imagise/entities';
import { ErrorReportingService } from '../error-reporting/error-reporting.service';
import { captureOperationRequest, checkOperationResponse } from '../error-reporting/operation-request';
import { ImageCommandsService } from '../command/image-commands.service';
import { MessagingService } from './infrastructure/messaging/messaging.service';
import { RefreshEditOptionsMessage } from './domain/models/messages';
import { EditorOptions } from './domain/interfaces/model-options';
import { Size2D } from './domain/interfaces/shared';
import { ViewerImageInfo, ViewerService } from '../viewer/viewer.service';
import {
  AnimationSessionService,
  EMPTY_EDITOR_CANVAS_VIEW_STATE,
  EditorAnimationCanvasPreview,
  EditorBackendPreviewOrchestrator,
  EditorAnimationFrame,
  EditorCanvasViewState,
  EditorFormStore,
  EditorMergeDraftOperation,
  EditorMergeImage,
  MergeApiCall,
  MERGE_OUTPUT_FORMAT,
  MergeCanvasPreview,
  MergePreviewComposer,
  buildMergeApiCall,
  getAppliedImageSize,
  getEditorCanvasViewState,
  isMeaningfulMergeDraft,
  resolveEditorAnimationCanvasPreview,
  resolveEditorCanvasPreviewState,
  resolveMergeLayout,
  ResolvedEditorCanvasPreviewState,
} from './domain';
import { AnimationFramePlayer } from './domain/animation-frame-player';
import { getEditorAnalyticsErrorCategory } from './domain/editor-analytics';
import {
  EditorPagesController,
  ImageEditorViewPageInfo as PagesViewPageInfo,
} from './domain/editor-pages.controller';
import {
  buildMergedFileName,
  createFileResultFromBlob,
  extractBase64Payload,
} from './domain/file-result.factory';
import {
  canShowNativeAnimatedSourcePreview,
  getNativeAnimatedSourceUrl,
} from './domain/gif-source.utils';
import {
  AnimationFrameDurationEvent,
  AnimationFramePanelComponent,
  AnimationFrameReorderEvent,
} from './ui/animation-frame-panel';
import { DownloadDialogComponent } from './ui/download-dialog/download-dialog.component';
import { FileUploaderComponent } from './ui/file-uploader/file-uploader.component';
import {
  MergeImagePanelComponent,
  MergeImagePositionEvent,
  MergeImageReorderEvent,
} from './ui/merge-image-panel';
import { SubMenuComponent } from './ui/sub-menu/sub-menu.component';
import { SvgCanvasComponent } from './ui/svg-canvas/svg-canvas.component';
import { TopPanelComponent } from './ui/top-panel/top-panel.component';
import { toObjectDetectionBox, type ObjectDetectionBox } from './infrastructure/rendering';

@Component({
  selector: 'lib-image-editor',
  templateUrl: './image-editor.component.html',
  providers: [EditorFormStore],
  imports: [
    EditorShellComponent,
    EditorWorkspaceComponent,
    MainMenuComponent,
    SubMenuComponent,
    TopPanelComponent,
    SvgCanvasComponent,
    FileUploaderComponent,
    MergeImagePanelComponent,
    AnimationFramePanelComponent,
  ],
})
export class ImageEditorComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  private readonly viewerService = inject(ViewerService);
  private readonly messageBus = inject(MessagingService);
  private readonly commandSvc = inject(ImageCommandsService);
  private readonly editorStore = inject(EditorFormStore);
  readonly #editService = inject(EditService);
  readonly #mergeService = inject(MergeService);
  readonly #objectDetectionService = inject(VisionObjectDetectionService);
  private readonly animationSession = inject(AnimationSessionService);
  private readonly analytics = inject(AnalyticsService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly errorReporting = inject(ErrorReportingService);

  private readonly subscriptions = new Subscription();
  private readonly backendPreviewOrchestrator = new EditorBackendPreviewOrchestrator({
    requestPreview: request => {
      const operation = captureOperationRequest('preview', '/edit', serializeEditImageRequest(request), this.operationImageNames());
      return dispatchEditImageRequest(this.#editService, request).pipe(
        checkOperationResponse(),
        tap({ error: error => this.errorReporting.reportOperationError(error, operation) }),
      );
    },
  });

  private editorSnapshot = this.editorStore.snapshot();
  private activeCanvasState: ResolvedEditorCanvasPreviewState = resolveEditorCanvasPreviewState(
    this.editorSnapshot.source.previewUrl,
    getEditorCanvasViewState(this.editorSnapshot),
    this.backendPreviewOrchestrator.snapshot()
  );
  private appliedImageSize: Size2D | null = null;
  private animationCanvasPreview: EditorAnimationCanvasPreview | null = null;
  private readonly framePlayer = new AnimationFramePlayer({
    runOutsideZone: fn => this.zone.runOutsideAngular(fn),
    onFrameAdvanced: () => this.changeDetector.detectChanges(),
    canSchedule: this.isBrowser,
  });
  private readonly pages = new EditorPagesController<ViewerImageInfo>({
    loadPage: (file, pageIndex) => {
      const attachment = file.FileBlob ? { name: file.FileName, blob: file.FileBlob } :
        file.SourceBase64 ? attachmentFromBase64(file.SourceBase64, file.FileName, file.SourceMimeType) : null;
      const needsDownload = !file.DownloadUrl && !file.UploadFileName && !file.LocalObjectUrl && !file.SourceBase64;
      const operation: ErrorReportOperation = {
        operation: 'view-image', parameters: { pageIndex }, files: attachment ? [attachment] : [],
        ...(needsDownload ? { endpoint: '/download/{id}', method: 'GET' } : {}),
      };
      return this.viewerService.viewImage(file, pageIndex, ['hideAnimationFrames']).pipe(
        tap({ error: error => this.errorReporting.reportOperationError(error, operation) }),
      );
    },
    unwrapAppError: error => this.unwrapAppError(error),
    onLoadingChanged: loading => this.editorStore.setLoading(loading),
    onError: error => this.editorStore.setError(error),
    onPageCount: count => {
      this.commandSvc.pageCount = count;
    },
    onRequestedPagePreview: info => {
      this.editorStore.setSourcePreview(info.url);
      this.editorStore.setSourceDimensions(info.width, info.height);
    },
    onActivePageReady: () => {
      this.refreshEditOptionsDeferred();
    },
  });
  animationFrames: EditorAnimationFrame[] = [];
  mergeImages: EditorMergeImage[] = [];
  mergeLayout: EditorMergeDraftOperation['params']['layout'] | null = null;
  readonly editModes = EditMode;

  private readonly mergePreviewComposer = new MergePreviewComposer();
  private mergePreview: MergeCanvasPreview | null = null;
  private mergePreviewKey: string | null = null;
  private seedingCustomMergeLayout = false;
  mergeArrangeTiles: EditorArrangeTileInput[] | null = null;
  private mergeArrangeVersion = 0;
  private lastMergeLayout: EditorMergeDraftOperation['params']['layout'] | null = null;

  detectedObjects: ObjectDetectionBox[] | null = null;
  detectionLoading = false;
  detectionErrorKey: string | null = null;
  detectionDownloadUrl: string | null = null;
  private detectionSubscription: Subscription | null = null;
  private detectionVersion = 0;
  private readonly detectionJsonUrl = new ObjectUrlHolder();

  get animationPreviewPlaying(): boolean {
    return this.framePlayer.playing;
  }

  get previewPages(): ImageEditorViewPageInfo[] {
    return this.pages.previewPages;
  }

  get pageCount(): number {
    return this.pages.pageCount;
  }

  private readonly previewComponents = viewChildren<SvgCanvasComponent>('preview');

  constructor() {
    this.backendPreviewOrchestrator.connect(this.editorStore.state$);
    this.syncActiveCanvasState();
    this.syncAnimationCanvasPreview();
    this.subscriptions.add(
      this.editorStore.state$.subscribe(snapshot => {
        const previous = this.editorSnapshot;
        if (
          snapshot.source.sourceBase64 !== previous.source.sourceBase64 ||
          snapshot.source.previewUrl !== previous.source.previewUrl ||
          snapshot.ui.activeMode !== previous.ui.activeMode ||
          snapshot.ui.loading ||
          snapshot.history.cursor !== previous.history.cursor ||
          JSON.stringify(snapshot.committedEdits) !== JSON.stringify(previous.committedEdits)
        ) {
          this.cancelObjectDetection();
        }
        this.editorSnapshot = snapshot;
        this.animationFrames = snapshot.draft?.kind === 'animation' ? snapshot.draft.params.frames : [];
        this.mergeImages = snapshot.draft?.kind === 'merge' ? snapshot.draft.params.images : [];
        this.mergeLayout = snapshot.draft?.kind === 'merge' ? snapshot.draft.params.layout : null;
        this.syncActiveCanvasState();
        this.syncAnimationCanvasPreview();
        this.syncMergeCanvasPreview();
        // Tool pages are OnPush: async store updates (upload/preview callbacks)
        // must mark the path dirty, otherwise the editor view is skipped until
        // the next pointer event inside the page.
        this.changeDetector.markForCheck();
      })
    );
    this.subscriptions.add(
      this.backendPreviewOrchestrator.state$.subscribe(() => {
        this.syncActiveCanvasState();
        this.changeDetector.markForCheck();
      })
    );
  }

  public uploadProcess = false;

  readonly fileUploaded = output<FileResult | null>();


  private _disabled!: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.cancelObjectDetection();
    }
    if (value && !this.hasEditorOptionsMode) {
      this.editorStore.setActiveMode(EditMode.None);
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  private _imageFile: FileResult | null = null;
  @Input() set imageFile(value: FileResult | null) {
    this.cancelObjectDetection();
    this._imageFile = value;
    this.editorStore.setSourceImage(value);

    if (this.hasEditorOptionsMode && this.editorOptionsMode) {
      this.editorStore.setActiveMode(this.editorOptionsMode);
      this.ensureModeDraft(this.editorOptionsMode);
    } else {
      this.editorStore.setActiveMode(EditMode.None);
    }

    this.editorStore.setLoading(false);
    this.editorStore.setError(null);
    this.animationFrames = [];
    this.mergeImages = [];
    this.framePlayer.setPlaying(this.editorOptionsMode !== EditMode.ObjectDetection);
    this.pages.reset(value);
  }
  get imageFile(): FileResult | null {
    return this._imageFile;
  }

  readonly showLoader = input(false);

  readonly toolSlug = input.required<string>();

  readonly allowLocalFileUpload = input(false);

  readonly editorOptions = input<EditorOptions | null>(null);

  get editorOptionsMode(): EditMode | null {
    const editorOptions = this.editorOptions();
    if (!editorOptions) return null;
    const modeKey = editorOptions.editMode as keyof typeof EditMode;
    const modeValue = EditMode[modeKey];
    return typeof modeValue === 'number' ? modeValue : EditMode.None;
  }

  get hasEditorOptionsMode(): boolean {
    return !!this.editorOptions() && this.editorOptionsMode !== EditMode.None;
  }

  get hasImageFile(): boolean {
    return this.imageFile != null;
  }

  get hasActiveImage(): boolean {
    return this.pages.hasActiveImage;
  }

  get showSpinner(): boolean {
    return this.editorStore.form.controls.ui.controls.loading.getRawValue();
  }

  get editMode(): EditMode {
    return this.editorStore.form.controls.ui.controls.activeMode.getRawValue();
  }

  get imageSize(): Size2D | null {
    if (this.mergePreview) {
      return {
        width: this.mergePreview.width,
        height: this.mergePreview.height,
      };
    }

    return this.appliedImageSize;
  }

  get showOverlay(): boolean {
    // Empty-state uploads use the uploader-local spinner; once an image is
    // loaded, merge/animation/apply flows still rely on the shell overlay.
    return this.showLoader() || (this.uploadProcess && this.hasImageFile);
  }

  get showSubMenu(): boolean {
    // EditMode.None has no panel content (the sub-menu switch renders
    // nothing), so the submenu column must collapse with the panel — also on
    // tool pages, where the page-configured mode comes back via the main menu.
    return this.editMode !== EditMode.None && !this.disabled && !this.showSpinner;
  }

  get canToggleAnimationPreview(): boolean {
    if (this.editMode === EditMode.ObjectDetection) {
      return false;
    }
    return Boolean(this.animationCanvasPreview) || this.canShowNativeSourcePreview();
  }

  get canDetectObjects(): boolean {
    const page = this.previewPages.find(item => item.isActive);
    const preview = this.getActivePreview();
    return this.isBrowser && this.editMode === EditMode.ObjectDetection &&
      !this.disabled && !this.uploadProcess && !this.showSpinner && !this.detectionLoading &&
      !this.activeCanvasState.loading && !this.activeCanvasState.error && !this.editorSnapshot.ui.error &&
      Boolean(page?.pageInfo && preview?.imageUrl === this.resolveCanvasImageUrl(page) && preview?.canExportCommittedPng);
  }

  private canShowNativeSourcePreview(): boolean {
    return canShowNativeAnimatedSourcePreview(
      this.activeCanvasState.viewState,
      this.imageFile,
      this.activeCanvasState.imageUrl
    );
  }

  ngOnInit(): void {
    this.restoreAnimationSession();
  }

  ngOnDestroy(): void {
    this.cancelObjectDetection();
    this.persistAnimationSession();
    this.framePlayer.destroy();
    this.subscriptions.unsubscribe();
    this.backendPreviewOrchestrator.destroy();
    this.mergePreviewComposer.dispose();
  }

  get isAnimationTool(): boolean {
    return this.editorOptionsMode === EditMode.Animation;
  }

  get isMergeTool(): boolean {
    return this.editorOptionsMode === EditMode.Merge;
  }

  get allowMultipleUpload(): boolean {
    return this.isAnimationTool || this.isMergeTool;
  }

  /**
   * The editor is recreated on every tool-page navigation; for the Animation
   * Maker the collected frames are restored from the cross-page session so
   * leaving the tool and coming back does not lose them.
   */
  private restoreAnimationSession(): void {
    if (!this.isAnimationTool || this.imageFile) {
      return;
    }

    const session = this.animationSession.restore();
    if (!session) {
      return;
    }

    this.imageFile = session.imageFile;
    this.editorStore.restoreAnimationDraft(session.draft);
  }

  private persistAnimationSession(): void {
    const draft = this.editorStore.getAnimationDraftSnapshot();

    if (this.imageFile && draft && draft.params.frames.length > 1) {
      this.animationSession.save({ imageFile: this.imageFile, draft });
    } else if (this.isAnimationTool) {
      // Leaving the animation tool with no frames forgets the saved session;
      // other tools must not wipe it on their own destroy.
      this.animationSession.clear();
    }
  }

  openDownloadWindow(): void {
    if (!this.imageFile || this.uploadProcess) return;

    // A configured but unapplied merge must reach the download too — bake it
    // into the source image first, exactly like the Apply button does.
    if (isMeaningfulMergeDraft(this.editorSnapshot.draft)) {
      this.applyMergeDraft().subscribe({
        next: () => this.openDownloadDialog(),
        error: error => this.handleImageProcessingError(error),
      });
      return;
    }

    this.openDownloadDialog();
  }

  onEditModeChanged(editMode: EditMode): void {
    if (this.uploadProcess) {
      return;
    }

    if (editMode === EditMode.ObjectDetection) {
      this.framePlayer.setPlaying(false);
    }
    this.editorStore.setActiveMode(editMode);
    this.ensureModeDraft(editMode);
  }

  onClosePanelRequest(): void {
    this.onEditModeChanged(EditMode.None);
  }

  onUploadProcess(process: boolean): void {
    if (process) {
      this.cancelObjectDetection();
    }
    this.uploadProcess = process;
  }

  onDetectObjects(): void {
    if (!this.canDetectObjects) {
      return;
    }

    this.cancelObjectDetection();
    const preview = this.getActivePreview();
    const imageBase64 = preview?.exportCommittedPngBase64();
    if (!imageBase64) {
      this.detectionErrorKey = 'EDITOR.OBJECT_DETECTION.IMAGE_ERROR';
      return;
    }
    const imageSize = preview?.imageSize;
    if (imageBase64.length > Math.ceil(8 * 1024 * 1024 / 3) * 4 ||
        (imageSize && imageSize.width * imageSize.height > 40_000_000)) {
      this.detectionErrorKey = 'EDITOR.OBJECT_DETECTION.IMAGE_TOO_LARGE';
      return;
    }

    const version = this.detectionVersion;
    const operation = captureOperationRequest('detect-objects', '/detect-objects/ai', { imageBase64 }, ['detection-input.png']);
    this.detectionLoading = true;
    this.detectionSubscription = this.#objectDetectionService.detectObjectsAi({ imageBase64 })
      .pipe(checkOperationResponse(), map(result => {
        if (!Array.isArray(result.objects)) throw new Error('Object detection returned no result.');
        return result;
      }), finalize(() => {
        if (version === this.detectionVersion) {
          this.detectionLoading = false;
          this.changeDetector.markForCheck();
        }
      }))
      .subscribe({
        next: result => {
          if (version !== this.detectionVersion) {
            return;
          }
          this.detectedObjects = result.objects.flatMap(object => {
            const box = toObjectDetectionBox(object);
            return box ? [box] : [];
          });
          this.detectionDownloadUrl = this.detectionJsonUrl.create(new Blob([
            JSON.stringify({ coordinateScale: 1000, imageWidth: imageSize?.width, imageHeight: imageSize?.height, ...result }, null, 2),
          ], { type: 'application/json' }));
        },
        error: (error: unknown) => {
          if (version !== this.detectionVersion) {
            return;
          }
          const status = (error as { status?: number } | null)?.status;
          const key = status === 413 || status === 415 ? 'IMAGE_TOO_LARGE' :
            status === 429 ? 'BUSY' : status === 503 ? 'UNAVAILABLE' : status === 504 ? 'TIMEOUT' : 'FAILED';
          this.detectionErrorKey = `EDITOR.OBJECT_DETECTION.${key}`;
          this.errorReporting.reportOperationError(error, operation);
        },
      });
  }

  cancelObjectDetection(): void {
    this.detectionVersion += 1;
    this.detectionSubscription?.unsubscribe();
    this.detectionSubscription = null;
    this.detectionLoading = false;
    this.detectedObjects = null;
    this.detectionErrorKey = null;
    this.detectionDownloadUrl = null;
    this.detectionJsonUrl.revoke();
  }

  onFileUploaded(uploadedFile: FileResult | null): void {
    if (uploadedFile) {
      this.imageFile = uploadedFile;
    }

    this.fileUploaded.emit(uploadedFile);
  }

  onExtraFilesSelected(files: File[]): void {
    if (this.isMergeTool) {
      this.onMergeImageFilesSelected(files);
    } else {
      this.onAnimationFrameFilesSelected(files);
    }
  }

  onMergeImageFilesSelected(files: File[]): void {
    if (!files.length) {
      return;
    }

    this.editorStore.ensureMergeDraft();
    this.uploadProcess = true;
    from(files)
      .pipe(
        concatMap(file => this.readMergeImageFile(file)),
        finalize(() => {
          this.uploadProcess = false;
        })
      )
      .subscribe({
        next: image => {
          this.editorStore.addMergeImages([image]);
        },
        error: error => this.handleImageProcessingError(error),
      });
  }

  onMergeImageReordered(event: MergeImageReorderEvent): void {
    this.editorStore.moveMergeImage(event.id, event.position);
  }

  onMergeImagePositionChanged(event: MergeImagePositionEvent): void {
    this.editorStore.setMergeImagePosition(event.id, event.x, event.y);
  }

  onMergeImageDeleted(id: string): void {
    this.editorStore.deleteMergeImage(id);
  }

  onMergeImageDuplicated(id: string): void {
    this.editorStore.duplicateMergeImage(id);
  }

  onMergeApplied(): void {
    if (this.uploadProcess) {
      return;
    }

    this.applyMergeDraft().subscribe({
      error: error => this.handleImageProcessingError(error),
    });
  }

  onAnimationFrameFilesSelected(files: File[]): void {
    if (!files.length) {
      return;
    }

    this.editorStore.ensureAnimationDraft();
    this.uploadProcess = true;
    from(files)
      .pipe(
        concatMap(file => this.readAnimationFrameFile(file)),
        finalize(() => {
          this.uploadProcess = false;
        })
      )
      .subscribe({
        next: frame => {
          this.editorStore.addAnimationFrames([frame]);
        },
        error: error => this.handleImageProcessingError(error),
      });
  }

  onAnimationFrameReordered(event: AnimationFrameReorderEvent): void {
    this.editorStore.moveAnimationFrame(event.id, event.position);
  }

  onAnimationFrameDurationChanged(event: AnimationFrameDurationEvent): void {
    this.editorStore.updateAnimationFrameDuration(event.id, event.durationMs);
  }

  onAnimationFrameDeleted(id: string): void {
    this.editorStore.deleteAnimationFrame(id);
  }

  onAnimationFrameDuplicated(id: string): void {
    this.editorStore.duplicateAnimationFrame(id);
  }

  toggleAnimationPreview(): void {
    if (!this.canToggleAnimationPreview) {
      return;
    }

    this.framePlayer.toggle();
    this.changeDetector.markForCheck();
  }

  resolveCanvasImageUrl(page: ImageEditorViewPageInfo): string | undefined {
    if (!page.isActive) {
      return page.pageInfo?.url;
    }

    if (this.mergePreview) {
      return this.mergePreview.url;
    }

    const animationFrame = this.animationCanvasPreview?.frames[this.framePlayer.frameIndex];
    if (animationFrame) {
      return animationFrame.previewUrl;
    }

    return this.activeCanvasState.imageUrl ?? page.pageInfo?.url;
  }

  resolveCanvasNativeAnimatedImageUrl(page: ImageEditorViewPageInfo): string | undefined {
    if (!page.isActive || page.error || !this.animationPreviewPlaying || this.animationCanvasPreview ||
        this.editMode === EditMode.ObjectDetection) {
      return undefined;
    }

    if (!this.canShowNativeSourcePreview()) {
      return undefined;
    }

    return (
      getNativeAnimatedSourceUrl(this.imageFile) ?? this.activeCanvasState.imageUrl ?? page.pageInfo?.url
    );
  }

  resolveCanvasViewState(page: ImageEditorViewPageInfo): EditorCanvasViewState {
    if (!page.isActive) {
      return EMPTY_EDITOR_CANVAS_VIEW_STATE;
    }

    // The merge preview is composed client-side into a plain image URL —
    // no committed edits or drafts apply on top of it.
    if (this.mergePreview) {
      return EMPTY_EDITOR_CANVAS_VIEW_STATE;
    }

    return this.animationCanvasPreview?.viewState ?? this.activeCanvasState.viewState;
  }

  resolveCanvasError(page: ImageEditorViewPageInfo): AppError | undefined {
    if (!page.isActive) {
      return page.error;
    }

    return page.error ?? this.activeCanvasState.error ?? this.editorSnapshot.ui.error ?? undefined;
  }

  resolveCanvasLoading(page: ImageEditorViewPageInfo): boolean {
    // the animation is rendered fully client-side — a pending backend preview
    // (e.g. deskew) must not cover the running frames with a spinner
    if (this.animationCanvasPreview) {
      return false;
    }

    return (
      page.isActive &&
      (this.activeCanvasState.loading || this.showSpinner || (this.uploadProcess && this.hasImageFile))
    );
  }

  private refreshEditOptions(): void {
    this.messageBus.publish(new RefreshEditOptionsMessage());
  }

  private refreshEditOptionsDeferred(): void {
    setTimeout(() => {
      this.refreshEditOptions();
    }, 30);
  }

  private ensureModeDraft(mode: EditMode): void {
    if (mode === EditMode.Animation) {
      this.editorStore.ensureAnimationDraft();
    } else if (mode === EditMode.Merge) {
      this.editorStore.ensureMergeDraft();
    }
  }

  private readAnimationFrameFile(file: File): Observable<Omit<EditorAnimationFrame, 'id'>> {
    return readFile(file).pipe(
      map(dataUrl => ({
        name: file.name,
        sourceBase64: extractBase64Payload(dataUrl),
        previewUrl: dataUrl,
        mimeType: file.type || null,
        durationMs: 500,
      })),
      tap({ error: error => this.errorReporting.reportOperationError(error, {
        operation: 'read-animation-frame', files: [{ name: file.name, blob: file }],
      }) }),
    );
  }

  private readMergeImageFile(file: File): Observable<Omit<EditorMergeImage, 'id'>> {
    return readFile(file).pipe(
      map(dataUrl => ({
        name: file.name,
        sourceBase64: extractBase64Payload(dataUrl),
        previewUrl: dataUrl,
        mimeType: file.type || null,
        x: 0,
        y: 0,
      })),
      tap({ error: error => this.errorReporting.reportOperationError(error, {
        operation: 'read-merge-image', files: [{ name: file.name, blob: file }],
      }) }),
    );
  }

  /**
   * Bakes the merge draft through the /merge/* endpoints: the resulting blob
   * replaces the editor source image, so every other tool keeps working on
   * the merged picture.
   */
  private applyMergeDraft(): Observable<FileResult> {
    const draft = this.editorSnapshot.draft;

    if (!isMeaningfulMergeDraft(draft)) {
      return throwError(() => new Error('Merge requires at least two images.'));
    }

    const analyticsParameters = {
      tool_slug: this.toolSlug(),
      operation: 'merge' as const,
      output_format: MERGE_OUTPUT_FORMAT,
    };
    this.analytics.track('processing_start', analyticsParameters);
    this.uploadProcess = true;

    return from(this.mergePreviewComposer.measureImageSizes(draft.params.images)).pipe(
      map(sizes => buildMergeApiCall(draft.params, sizes)),
      switchMap(call => this.executeMergeCall(call, draft.params.images.map(image => image.name))),
      switchMap(blob =>
        createFileResultFromBlob(
          blob,
          buildMergedFileName(this.imageFile?.FileName),
          this.imageFile?.idUpload || ''
        )
      ),
      map(fileResult => {
        this.imageFile = fileResult;
        this.fileUploaded.emit(fileResult);
        return fileResult;
      }),
      tap({
        next: () => this.analytics.track('processing_success', analyticsParameters),
        error: error =>
          this.analytics.track('processing_error', {
            ...analyticsParameters,
            error_category: getEditorAnalyticsErrorCategory(error),
          }),
      }),
      finalize(() => {
        this.uploadProcess = false;
      })
    );
  }

  private executeMergeCall(call: MergeApiCall, imageNames: readonly string[]): Observable<Blob> {
    const endpoint = `/merge/to-single-page${call.kind === 'single' ? '' : `/${call.kind}`}`;
    const operation = captureOperationRequest('merge', endpoint, call.request, imageNames);
    let result: Observable<Blob>;
    switch (call.kind) {
      case 'grid':
        result = this.#mergeService.mergeSinglePageByGrid(call.request as GridMergeImageRequest);
        break;
      case 'flex':
        result = this.#mergeService.mergeSinglePageByFlow(
          call.request as unknown as FlexMergeImageRequest,
        );
        break;
      case 'single':
        result = this.#mergeService.mergeSinglePage(call.request as MergeImageRequest);
        break;
    }
    return result.pipe(checkOperationResponse(), tap({
      error: error => this.errorReporting.reportOperationError(error, operation),
    }));
  }

  private operationImageNames(): string[] {
    const snapshot = this.editorStore.snapshot();
    return [snapshot.source.fileName, ...snapshot.committedEdits.slice(0, snapshot.history.cursor)
      .flatMap(edit => edit.kind === 'animation' ? edit.params.frames.slice(1).map(frame => frame.name) : [])];
  }

  private handleImageProcessingError(error: unknown): void {
    const xhr = error as ErrorResponse;
    const errorObject = xhr?.error
      ? this.unwrapAppError(xhr.error)
      : {
          ExceptionMessage: 'Image operation failed.',
          Type: 'ImageOperationError',
          Details: 'Image operation failed.',
        };

    this.editorStore.setError(errorObject);
  }

  private openDownloadDialog(): void {
    if (!this.imageFile) return;

    DownloadDialogComponent.open(this.dialog, {
      toolSlug: this.toolSlug(),
      uploadedFileName: this.imageFile.FileName,
      sourceFile: this.imageFile,
      editorStore: this.editorStore,
    });
  }

  private getActivePreview(): SvgCanvasComponent | undefined {
    return this.previewComponents()?.find(component => component.isActive);
  }

  private unwrapAppError(errorObject: AppError): AppError {
    const maybeWrapped = errorObject as AppError & { Error?: AppError };
    return maybeWrapped.Error ?? errorObject;
  }

  private syncAnimationCanvasPreview(): void {
    this.animationCanvasPreview = resolveEditorAnimationCanvasPreview(this.editorSnapshot);
    this.framePlayer.setFrames(this.animationCanvasPreview?.frames ?? []);
  }

  private syncMergeCanvasPreview(): void {
    const draft = this.editorSnapshot.draft;

    if (!this.isBrowser || !isMeaningfulMergeDraft(draft)) {
      this.mergePreviewKey = null;
      this.mergePreview = null;
      this.mergeArrangeVersion += 1;
      this.mergeArrangeTiles = null;
      this.lastMergeLayout = null;
      return;
    }

    const enteredCustomLayout =
      draft.params.layout === 'custom' && this.lastMergeLayout !== 'custom';
    this.lastMergeLayout = draft.params.layout;

    if (enteredCustomLayout) {
      this.seedCustomMergeLayoutIfNeeded(draft);
    }
    this.syncMergeArrangeTiles(draft);

    const key = this.createMergePreviewKey(draft);
    if (key === this.mergePreviewKey) {
      return;
    }

    this.mergePreviewKey = key;
    this.mergePreviewComposer
      .compose(draft.params)
      .then(preview => {
        if (!preview || this.mergePreviewKey !== key) {
          return;
        }

        this.mergePreview = preview;
        this.changeDetector.markForCheck();
      })
      .catch(() => {
        if (this.mergePreviewKey === key) {
          this.mergePreview = null;
          this.changeDetector.markForCheck();
        }
      });
  }

  /**
   * Entering the custom layout with untouched positions would stack every
   * image at the origin — pre-arrange them once with the grid layout instead.
   * Runs only on the layout TRANSITION: positions the user set afterwards
   * (including deliberately stacking everything at 0,0) are never touched.
   */
  private seedCustomMergeLayoutIfNeeded(draft: EditorMergeDraftOperation): void {
    const params = draft.params;
    const needsSeed =
      params.images.length > 1 &&
      params.images.every(image => image.x === 0 && image.y === 0);

    if (!needsSeed || this.seedingCustomMergeLayout) {
      return;
    }

    this.seedingCustomMergeLayout = true;
    this.mergePreviewComposer
      .measureImageSizes(params.images)
      .then(sizes => {
        const layout = resolveMergeLayout({ ...params, layout: 'grid' }, sizes);
        const positions = new Map(
          layout.tiles.map(tile => [
            params.images[tile.imageIndex].id,
            { x: tile.bounds.x, y: tile.bounds.y },
          ])
        );
        this.editorStore.setMergeImagePositions(positions);
      })
      .catch(() => undefined)
      .finally(() => {
        this.seedingCustomMergeLayout = false;
      });
  }

  private createMergePreviewKey(draft: EditorMergeDraftOperation): string {
    const { images, ...settings } = draft.params;
    return JSON.stringify({
      ...settings,
      images: images.map(image => ({ id: image.id, x: image.x, y: image.y })),
    });
  }

  /**
   * The custom layout swaps the flat preview for draggable canvas tiles;
   * dropping a tile commits its position back into the draft.
   */
  private syncMergeArrangeTiles(draft: EditorMergeDraftOperation): void {
    const version = ++this.mergeArrangeVersion;

    if (draft.params.layout !== 'custom') {
      this.mergeArrangeTiles = null;
      return;
    }

    this.mergePreviewComposer
      .resolveArrangeTiles(draft.params)
      .then(tiles => {
        if (version !== this.mergeArrangeVersion) {
          return;
        }

        this.mergeArrangeTiles = tiles;
        this.changeDetector.markForCheck();
      })
      .catch(() => {
        if (version === this.mergeArrangeVersion) {
          this.mergeArrangeTiles = null;
          this.changeDetector.markForCheck();
        }
      });
  }

  onMergeTileDragged(event: { id: string; x: number; y: number }): void {
    this.editorStore.setMergeImagePosition(event.id, event.x, event.y);
  }

  private syncActiveCanvasState(): void {
    this.activeCanvasState = resolveEditorCanvasPreviewState(
      this.editorSnapshot.source.previewUrl,
      getEditorCanvasViewState(this.editorSnapshot),
      this.backendPreviewOrchestrator.snapshot()
    );
    this.appliedImageSize = this.getStableImageSize(getAppliedImageSize(this.editorSnapshot));
  }

  private getStableImageSize(nextSize: Size2D | null): Size2D | null {
    if (
      this.appliedImageSize &&
      nextSize &&
      this.appliedImageSize.width === nextSize.width &&
      this.appliedImageSize.height === nextSize.height
    ) {
      return this.appliedImageSize;
    }

    return nextSize;
  }
}

export type ImageEditorViewPageInfo = PagesViewPageInfo<ViewerImageInfo>;
